import './index.less';

import { ComponentProps, ReactNode, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import ArrowRight from '@/assets/svg/arrow-right.svg?react';
import UnderlineToolTip from '@/components/ToolTip/UnderlineToolTip';
import { EARN_TYPE } from '@/constants/earn';
import { TRADE_TYPE } from '@/constants/trade';
import { WrappedBigNumber } from '@/entities/WrappedBigNumber';
import { WrappedPair } from '@/entities/WrappedPair';
import { useMainPosition } from '@/features/account/positionHook';
import { useEarnFormType, useRemoveLiquidationSimulation } from '@/features/earn/hook';
import {
  useAdjustMarginSimulation,
  useCloseSimulation,
  useCrossMarketSimulation,
  useMarketSimulation,
  useSimulationBalanceChange,
  useTradeStoreTransformed,
  useTradeType,
} from '@/features/trade/hooks';
import { useWalletConnectStatus } from '@/features/wallet/hook';
import { useChainId, useUserAddr } from '@/hooks/web3/useChain';
import BalanceMax from '@/pages/components/BalanceMax';
import WrapETHButton from '@/pages/trade/TradeCard/TradeForm/WrapETHButton';
import { WALLET_CONNECT_STATUS } from '@/types/wallet';
import { useLocation } from 'react-router-dom';
import { EmptyDataWrap } from '../EmptyDataWrap';
type AvailableBalanceToolTipProps = {
  gateBalanceBeforeNode?: ReactNode;
  gateBalanceAfterNode: ReactNode;
  walletBalanceBeforeNode?: ReactNode;
  walletBalanceAfterNode: ReactNode;
};
const AvailableBalanceToolTip = ({
  gateBalanceBeforeNode,
  gateBalanceAfterNode,
  walletBalanceBeforeNode,
  walletBalanceAfterNode,
}: AvailableBalanceToolTipProps): JSX.Element => {
  const { t } = useTranslation();

  return (
    <div className="syn-available-balance-tooltip">
      <div className="syn-available-balance-tooltip-row">
        <span className="syn-available-balance-tooltip-row-left">{t('common.tradePage.accountB')}</span>
        <span className="syn-available-balance-tooltip-row-right">
          {gateBalanceBeforeNode && (
            <>
              <span className="syn-available-balance-tooltip-text-blue">{gateBalanceBeforeNode}</span>
              <ArrowRight />
            </>
          )}

          {gateBalanceAfterNode}
        </span>
      </div>
      <div className="syn-available-balance-tooltip-row">
        <span className="syn-available-balance-tooltip-row-left">{t('common.wb')}</span>
        <span className="syn-available-balance-tooltip-row-right">
          {walletBalanceBeforeNode && (
            <>
              <span className="syn-available-balance-tooltip-text-blue">{walletBalanceBeforeNode}</span>
              <ArrowRight />
            </>
          )}

          {walletBalanceAfterNode}
        </span>
      </div>
    </div>
  );
};
type AvailableBalanceProp = {
  balanceMaxProps?: ComponentProps<typeof BalanceMax>;
  hideBalanceMax?: boolean;
  showPosition?: boolean;
  currentPair: WrappedPair | undefined;
};
export const AvailableBalancePosition = ({
  balanceMaxProps,
  hideBalanceMax = true,
  showPosition = true,
  overrideTradeType,
  currentPair,
}: AvailableBalanceProp & { overrideTradeType?: TRADE_TYPE }): JSX.Element => {
  const { t } = useTranslation();
  const chainId = useChainId();
  const userAddr = useUserAddr();

  const walletStatus = useWalletConnectStatus();

  const currentPosition = useMainPosition(chainId, userAddr, currentPair?.id);
  const { pathname } = useLocation();
  const tradeTypeOrigin = useTradeType(chainId);
  const tradeType = overrideTradeType || tradeTypeOrigin;
  const adjustMarginSimulation = useAdjustMarginSimulation(chainId);
  const marketSimulation = useMarketSimulation(chainId);
  const earnType = useEarnFormType(chainId);
  const closePositionSimulation = useCloseSimulation(chainId);
  const marketState = useTradeStoreTransformed();
  const crossMarketSimulation = useCrossMarketSimulation(chainId);
  const removeLiquiditySimulation = useRemoveLiquidationSimulation(chainId);
  const removedNetPositionHasSize = useMemo(() => {
    return (
      !removeLiquiditySimulation?.data?.simulationMainPosition?.size?.sub(currentPosition?.size || 0)?.eq(0) &&
      !removeLiquiditySimulation?.data?.simulationMainPosition?.size?.eq(0)
    );
  }, [currentPosition?.size, removeLiquiditySimulation?.data?.simulationMainPosition.size]);
  const simulationPositionAfter = useMemo(() => {
    if (pathname.includes('/earn')) {
      if (earnType === EARN_TYPE.REMOVE_LIQ && removedNetPositionHasSize)
        return removeLiquiditySimulation?.data?.simulationMainPosition;
    } else if (tradeType === TRADE_TYPE.MARKET) {
      if (WrappedBigNumber.from(marketState.tradeSize).notEq(0)) {
        return marketSimulation?.data?.simulationMainPosition;
      }
    } else if (tradeType === TRADE_TYPE.ADJUST_MARGIN) {
      return adjustMarginSimulation?.data?.simulationMainPosition;
    } else if (tradeType === TRADE_TYPE.CLOSE_POSITION) {
      return closePositionSimulation?.data?.simulationMainPosition;
    } else if (tradeType === TRADE_TYPE.LIMIT) {
      return crossMarketSimulation?.data?.tradeSimulation?.simulationMainPosition;
    }
  }, [
    adjustMarginSimulation?.data?.simulationMainPosition,
    closePositionSimulation?.data?.simulationMainPosition,
    crossMarketSimulation?.data?.tradeSimulation?.simulationMainPosition,
    earnType,
    marketSimulation?.data?.simulationMainPosition,
    marketState.tradeSize,
    pathname,
    removeLiquiditySimulation?.data?.simulationMainPosition,
    removedNetPositionHasSize,
    tradeType,
  ]);
  const {
    gateBalanceBefore,
    gateBalanceAfter,
    walletBalanceBefore,
    walletBalanceAfter,
    totalBalanceBefore,
    totalBalanceAfter,
    isShowTotalBalanceChange,
  } = useSimulationBalanceChange(chainId, currentPair?.rootInstrument?.quoteToken, overrideTradeType);

  return (
    <div className="syn-available-balance-position">
      <dl>
        <dt>
          {t('common.availableBalanceTile.shortTitle')}
          {balanceMaxProps && <BalanceMax {...balanceMaxProps} />}
        </dt>
        <div className="syn-available-balance-position-divider" />
        <dd>
          <EmptyDataWrap loadingWrap="--" isLoading={walletStatus === WALLET_CONNECT_STATUS.UN_CONNECT}>
            {isShowTotalBalanceChange ? (
              <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                <span className="original">
                  <UnderlineToolTip
                    overlayStyle={{ width: '360px', minWidth: '360px' }}
                    overlayInnerStyle={{ width: '360px', padding: 16 }}
                    title={
                      <AvailableBalanceToolTip
                        gateBalanceBeforeNode={gateBalanceBefore?.formatNumberWithTooltip()}
                        gateBalanceAfterNode={gateBalanceAfter?.formatNumberWithTooltip({
                          suffix: currentPair?.rootInstrument.quoteToken.symbol,
                        })}
                        walletBalanceBeforeNode={walletBalanceBefore?.formatNumberWithTooltip()}
                        walletBalanceAfterNode={walletBalanceAfter?.formatNumberWithTooltip({
                          suffix: currentPair?.rootInstrument.quoteToken.symbol,
                        })}
                      />
                    }>
                    {totalBalanceBefore?.formatDisplayNumber()}
                  </UnderlineToolTip>
                </span>
                <ArrowRight />
                <span className="syn-available-balance-position-text-primary">
                  <span className="balance">{totalBalanceAfter?.formatDisplayNumber()}</span>
                  &nbsp;
                  {currentPair?.rootInstrument.quoteToken.symbol}
                </span>
              </div>
            ) : (
              <span className="syn-available-balance-position-text-primary">
                <UnderlineToolTip
                  overlayStyle={{ width: '300px', minWidth: '300px' }}
                  overlayInnerStyle={{ width: '300px', padding: 16 }}
                  title={
                    <AvailableBalanceToolTip
                      gateBalanceAfterNode={gateBalanceAfter?.formatNumberWithTooltip({
                        suffix: currentPair?.rootInstrument.quoteToken.symbol,
                      })}
                      walletBalanceAfterNode={walletBalanceAfter?.formatNumberWithTooltip({
                        suffix: currentPair?.rootInstrument.quoteToken.symbol,
                      })}
                    />
                  }>
                  <span className="balance">{totalBalanceAfter?.formatDisplayNumber()}</span>
                </UnderlineToolTip>
                &nbsp;
                {currentPair?.rootInstrument.quoteToken.symbol}
              </span>
            )}

            {!hideBalanceMax && <WrapETHButton quote={currentPair?.rootInstrument?.quoteToken} />}
          </EmptyDataWrap>
        </dd>
      </dl>
      {showPosition && (
        <dl>
          <dt>{t('common.availableBalanceTile.currentP')}</dt>
          <div className="syn-available-balance-position-divider" />
          <dd>
            <EmptyDataWrap loadingWrap="--" isLoading={walletStatus === WALLET_CONNECT_STATUS.UN_CONNECT}>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                {simulationPositionAfter &&
                !simulationPositionAfter?.size.eq(currentPosition?.size || 0) &&
                !simulationPositionAfter?.size.eq(0) ? (
                  <>
                    <span className="original">
                      {' '}
                      {currentPosition?.wrapAttribute('size')?.formatNumberWithTooltip({
                        isShowTBMK: true,
                        colorShader: false,
                        // colorSuffix: true,
                        suffix: !simulationPositionAfter ? currentPair?.rootInstrument.baseToken.symbol : '',
                      })}
                    </span>
                    <ArrowRight />
                    <span className="syn-available-balance-position-text-primary">
                      <span className="balance">
                        {simulationPositionAfter?.wrapAttribute('size')?.formatPriceNumberWithTooltip({
                          isShowTBMK: true,
                          colorShader: false,
                          // colorSuffix: true,
                          suffix: currentPair?.rootInstrument.baseToken.symbol,
                        })}
                      </span>
                    </span>
                  </>
                ) : (
                  <>
                    {' '}
                    {currentPosition?.wrapAttribute('size')?.formatNumberWithTooltip({
                      isShowTBMK: true,
                      colorShader: false,
                      // colorSuffix: true,
                      suffix: !simulationPositionAfter ? currentPair?.rootInstrument.baseToken.symbol : '',
                    })}
                  </>
                )}
              </div>
            </EmptyDataWrap>
          </dd>
        </dl>
      )}
    </div>
  );
};
